import React, { useState, useEffect, useContext, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import styled from "styled-components";
import {
  Steps,
  Card,
  Input,
  InputNumber,
  Select,
  Checkbox,
  message,
  Spin,
  Alert,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { FormError, InputHeader, Spacer } from "../styles/CommonElements";
import ButtonComponent from "../components/Button";
import PageHeader from "../components/PageHeader";
import { submitTaxPrep } from "../helpers/tax-prep-api";
import { downloadScheduleETaxPacketExcel } from "../utils/scheduleEExcelExport";
import { getCpaById } from "../lib/firebase/cpaModel";
import { cpaKeys } from "../lib/queryKeys";
import { device, useBreakpoints, LAYOUT_CONSTANTS } from "../lib/breakpoints";
import { formatEmailToLowercase } from "../utils/emailUtils";
import {
  APP_NAME,
  getDebugMode,
  OG_IMAGE_CACHE_VERSION,
  TAGLINE_SENTENCE,
} from "../helpers/constants";
import { AuthContext } from "../context/AuthProvider";
import { listLeases } from "../lib/api/leasesApi";
import { getExpensesByOwner } from "../lib/firebase/expenseModel";
import unitModel from "../lib/firebase/unitModel";
import { propertyKeys } from "../lib/queryKeys";
import { buildPortfolioTaxPrefill } from "../utils/taxPrepPortfolioPrefill";

/** Default CPA to recommend when user has no link - Firestore document ID */
const DEFAULT_RECOMMENDED_CPA_ID = "paytongillis";

const TAX_PREP_STORAGE_KEY = (cpaId) =>
  `landlordops-tax-prep-${cpaId || "default"}`;

/** True if saved draft has real user input (not just the default empty template). */
function taxPrepStorageHasMeaningfulDraft(storedJson) {
  try {
    const parsed = JSON.parse(storedJson);
    const v = parsed.formValues;
    if (!v) return false;
    const hasProp = (v.properties || []).some((p) => p?.address?.trim());
    if (hasProp) return true;
    if (String(v.totalRentCollected || "").trim()) return true;
    const ex = v.expenses || [];
    if (ex.length > 1) return true;
    if (ex[0]?.amount && ex[0]?.category) return true;
    if (String(v.userName || "").trim() || String(v.userEmail || "").trim())
      return true;
    return false;
  } catch {
    return false;
  }
}

const { Step } = Steps;
const { Option } = Select;

/** IRS Schedule E expense categories (plus Other for uncategorized) */
export const SCHEDULE_E_CATEGORIES = [
  { value: "Advertising", label: "Advertising" },
  { value: "Insurance", label: "Insurance" },
  { value: "Legal/Professional", label: "Legal/Professional" },
  { value: "Management Fees", label: "Management Fees" },
  { value: "Mortgage Interest", label: "Mortgage Interest" },
  { value: "Repairs", label: "Repairs" },
  { value: "Supplies", label: "Supplies" },
  { value: "Taxes", label: "Property Taxes" },
  { value: "Utilities", label: "Utilities" },
  { value: "Other Expenses", label: "Other Expenses" },
];

const STEPS = [
  { key: "property", title: "Properties", description: "Addresses" },
  { key: "income", title: "Income", description: "Rent" },
  { key: "expenses", title: "Expenses", description: "Deductions" },
  { key: "contact", title: "Contact", description: "Info" },
  { key: "review", title: "Review", description: "Submit" },
];

/** Demo form data when ?debug=true - one expense per category (plus a second Repairs to show aggregation). */
const TAX_PREP_DEMO_VALUES = {
  properties: [
    { address: "123 Main St, Seattle, WA 98101" },
    { address: "456 Oak Ave, Portland, OR 97201" },
  ],
  totalRentCollected: "54000",
  expenses: [
    { amount: "240", category: "Advertising" },
    { amount: "1200", category: "Insurance" },
    { amount: "450", category: "Legal/Professional" },
    { amount: "1620", category: "Management Fees" },
    { amount: "2100", category: "Mortgage Interest" },
    { amount: "850", category: "Repairs" },
    { amount: "320", category: "Repairs" },
    { amount: "180", category: "Supplies" },
    { amount: "450", category: "Taxes" },
    { amount: "600", category: "Utilities" },
    { amount: "275", category: "Other Expenses" },
  ],
  userName: "Jane Smith",
  userEmail: "jane@example.com",
};

/** Format number with commas for display; parse back to raw for form storage */
const currencyFormatter = (value) =>
  value != null && value !== ""
    ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    : "";
const currencyParser = (value) =>
  (value || "").replace(/\$\s?|(,*)/g, "") || "";

/** Build Schedule E pivot from tax prep form data for Excel export.
 * @param {string[]|{address?: string}[]} properties - Array of addresses (strings) or objects with address property
 */
const buildTaxPrepPivot = (properties, totalRentCollected, expenses) => {
  const props = (properties || [])
    .map((p) =>
      typeof p === "string"
        ? p.trim()
        : (p?.address || p?.propertyAddress || "").trim()
    )
    .filter(Boolean);
  if (props.length === 0) return null;

  const firstProp = props[0];
  const incomeRows = [
    "Rents Received",
    "Late Fees",
    "Other Income",
    "Security Deposits",
  ];
  const expenseRows = [
    "Advertising",
    "Insurance",
    "Legal/Professional",
    "Management Fees",
    "Mortgage Interest",
    "Repairs",
    "Supplies",
    "Taxes",
    "Utilities",
    "Other Expenses",
  ];

  const pivot = {};
  incomeRows.forEach((r) => {
    pivot[r] = {};
    props.forEach((p) => (pivot[r][p] = 0));
  });
  expenseRows.forEach((r) => {
    pivot[r] = {};
    props.forEach((p) => (pivot[r][p] = 0));
  });

  pivot["Rents Received"][firstProp] = totalRentCollected;
  (expenses || []).forEach((e) => {
    const cat =
      e.category && expenseRows.includes(e.category)
        ? e.category
        : "Other Expenses";
    const amt = parseFloat(String(e.amount).replace(/[^0-9.-]/g, "")) || 0;
    pivot[cat][firstProp] = (pivot[cat][firstProp] || 0) + amt;
  });

  const rows = [];
  incomeRows.forEach((row) => {
    const amounts = {};
    let total = 0;
    props.forEach((prop) => {
      amounts[prop] = pivot[row][prop] || 0;
      total += amounts[prop];
    });
    rows.push({ category: row, amounts, total });
  });
  expenseRows.forEach((row) => {
    const amounts = {};
    let total = 0;
    props.forEach((prop) => {
      amounts[prop] = pivot[row][prop] || 0;
      total += amounts[prop];
    });
    rows.push({ category: row, amounts, total });
  });

  const totalIncome = incomeRows.reduce(
    (s, r) => s + (rows.find((x) => x.category === r)?.total || 0),
    0
  );
  const totalExpenses = expenseRows.reduce(
    (s, r) => s + (rows.find((x) => x.category === r)?.total || 0),
    0
  );
  const totalIncomeByProp = {};
  const totalExpensesByProp = {};
  const netByProp = {};
  props.forEach((prop) => {
    totalIncomeByProp[prop] = incomeRows.reduce(
      (s, r) => s + (pivot[r]?.[prop] || 0),
      0
    );
    totalExpensesByProp[prop] = expenseRows.reduce(
      (s, r) => s + (pivot[r]?.[prop] || 0),
      0
    );
    netByProp[prop] = totalIncomeByProp[prop] - totalExpensesByProp[prop];
  });
  rows.push({
    category: "Total Income",
    amounts: totalIncomeByProp,
    total: totalIncome,
    isSubtotal: true,
  });
  rows.push({
    category: "Total Expenses",
    amounts: totalExpensesByProp,
    total: totalExpenses,
    isSubtotal: true,
  });
  rows.push({
    category: "Net Income (Loss)",
    amounts: netByProp,
    total: totalIncome - totalExpenses,
    isSubtotal: true,
  });

  return { rows, properties: props };
};

/** Placeholder-like value we should not show as the CPA name */
const CPA_NAME_PLACEHOLDER = /^\s*enter company name\s*$/i;

/** Format CPA display name for in-page use (name, company, or id). */
function formatCpaDisplayName(cpa) {
  const name = (cpa?.name || "").trim();
  if (name && !CPA_NAME_PLACEHOLDER.test(name)) return name;
  const company = (cpa?.company || "").trim();
  if (company && !CPA_NAME_PLACEHOLDER.test(company)) return company;
  const id = (cpa?.id || "").trim();
  return id ? id.charAt(0).toUpperCase() + id.slice(1).toLowerCase() : "";
}

/** Email for recommended CPA (Payton) in the "need a CPA" card */
const RECOMMENDED_CPA_EMAIL = "info@tgpfirm.com";

/** Improved metadata for tax prep (align with edge function for link previews). */
const TAX_PREP_META_TITLE =
  "Professional Tax Prep for Real Estate Investors | LandlordOps";
const TAX_PREP_META_DESCRIPTION = `Organize your rental income and expenses with ${APP_NAME}, ${TAGLINE_SENTENCE}. Automate tax prep and avoid the spreadsheet headache.`;

/** Meta + OG/Twitter for tax prep page. */
function TaxPrepHelmet({ taxYear, cpa }) {
  const cpaName = cpa ? formatCpaDisplayName(cpa) : null;
  const title = cpaName
    ? `Send your ${taxYear} Schedule E to ${cpaName} | LandlordOps`
    : TAX_PREP_META_TITLE;
  const description = cpaName
    ? `Organize your rental income and expenses for ${cpaName}. Automate your ${taxYear} tax prep and avoid the spreadsheet headache.`
    : TAX_PREP_META_DESCRIPTION;
  const url = typeof window !== "undefined" ? window.location.href : "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const ogImage = origin
    ? `${origin}/meta.png?v=${OG_IMAGE_CACHE_VERSION}`
    : "";

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {url && <link rel="canonical" href={url} />}
      <meta property="og:type" content="website" />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="LandlordOps" />
      <meta name="twitter:card" content="summary_large_image" />
      {url && <meta name="twitter:url" content={url} />}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}

/** Tax year = previous calendar year (e.g. in 2026 we prep for 2025) */
const getTaxYear = () => new Date().getFullYear() - 1;

const TaxPrepPage = () => {
  const taxYear = getTaxYear();
  const { user } = useContext(AuthContext);
  const { isMobile } = useBreakpoints();
  const [searchParams] = useSearchParams();
  const cpaId = searchParams.get("cpa") || "";
  const isTaxPrepDemo = getDebugMode();

  const {
    data: cpa,
    isFetched: cpaFetched,
    isLoading: cpaLoading,
  } = useQuery({
    queryKey: cpaKeys.byId(cpaId),
    queryFn: () => getCpaById(cpaId),
    enabled: !!cpaId,
  });

  /** CPA code in URL but no matching CPA in Firebase */
  const invalidCpa = !!cpaId && cpaFetched && !cpa;

  /** CPA code in URL and we're still fetching from Firebase */
  const loadingCpa = !!cpaId && cpaLoading;

  /** User has a valid CPA (code in URL and resolved from Firebase); show CPA UI and send to CPA on submit */
  const hasValidCpa = !!cpaId && !!cpa;

  const { data: recommendedCpa } = useQuery({
    queryKey: cpaKeys.byId(DEFAULT_RECOMMENDED_CPA_ID),
    queryFn: () => getCpaById(DEFAULT_RECOMMENDED_CPA_ID),
    enabled: !cpaId || (!!cpaId && cpaFetched && !cpa),
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedToUserOnly, setSubmittedToUserOnly] = useState(false);
  const [subscribeToEmails, setSubscribeToEmails] = useState(false);
  const portfolioPrefillAppliedRef = useRef(false);

  const { data: leases = [], isSuccess: leasesOk } = useQuery({
    queryKey: ["leases", user?.uid],
    queryFn: listLeases,
    enabled: !!user?.uid && !isTaxPrepDemo,
  });

  const { data: expensesResult, isSuccess: expensesOk } = useQuery({
    queryKey: propertyKeys.expenses(user?.uid),
    queryFn: () => getExpensesByOwner(),
    enabled: !!user?.uid && !isTaxPrepDemo,
  });

  const { data: units = [], isSuccess: unitsOk } = useQuery({
    queryKey: propertyKeys.units(user?.uid),
    queryFn: () => unitModel.getByOwner(user.uid),
    enabled: !!user?.uid && !isTaxPrepDemo,
  });

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    trigger,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      properties: [{ address: "" }],
      totalRentCollected: "",
      expenses: [{ amount: "", category: "" }],
      userName: "",
      userEmail: "",
    },
  });

  const formValues = watch();

  useEffect(() => {
    if (isTaxPrepDemo) return;
    const storageKey = cpaId || "default";
    try {
      const stored = localStorage.getItem(TAX_PREP_STORAGE_KEY(storageKey));
      if (stored) {
        const parsed = JSON.parse(stored);
        const {
          formValues: saved,
          currentStep: savedStep,
          subscribeToEmails: savedSubscribe,
        } = parsed;
        if (saved) {
          reset({
            properties:
              Array.isArray(saved.properties) && saved.properties.length > 0
                ? saved.properties
                : [{ address: "" }],
            totalRentCollected: saved.totalRentCollected ?? "",
            expenses:
              Array.isArray(saved.expenses) && saved.expenses.length > 0
                ? saved.expenses
                : [{ amount: "", category: "" }],
            userName: saved.userName ?? "",
            userEmail: saved.userEmail ?? "",
          });
        }
        if (typeof savedStep === "number" && savedStep >= 0 && savedStep <= 4) {
          setCurrentStep(savedStep);
        }
        if (typeof savedSubscribe === "boolean") {
          setSubscribeToEmails(savedSubscribe);
        }
      }
    } catch (e) {
      console.warn("Could not load tax prep from storage:", e);
    }
  }, [cpaId, isTaxPrepDemo]); // eslint-disable-line react-hooks/exhaustive-deps -- only load on cpaId change, skip when demo

  /** Prefill from unified leases (REST) + Firestore expenses (same as /expenses) when logged in and no saved draft */
  useEffect(() => {
    if (isTaxPrepDemo || !user?.uid || portfolioPrefillAppliedRef.current)
      return;
    const storageKey = cpaId || "default";
    try {
      const raw = localStorage.getItem(TAX_PREP_STORAGE_KEY(storageKey));
      if (raw && taxPrepStorageHasMeaningfulDraft(raw)) return;
    } catch {
      return;
    }
    if (!leasesOk || !expensesOk || !unitsOk) return;

    const prefill = buildPortfolioTaxPrefill({
      leases,
      expenses: expensesResult?.expenses || [],
      units,
      taxYear,
      userProfile: {
        displayName: user.displayName || "",
        email: user.email || "",
      },
    });

    if (!prefill.hasData) return;

    portfolioPrefillAppliedRef.current = true;
    reset({
      properties: prefill.properties,
      totalRentCollected: prefill.totalRentCollected,
      expenses: prefill.expenses,
      userName: prefill.userName,
      userEmail: prefill.userEmail,
    });
    message.success(
      `Imported your ${taxYear} rental data from Leases and Expenses. Review all amounts before submitting.`
    );
  }, [
    isTaxPrepDemo,
    user?.uid,
    user?.displayName,
    user?.email,
    cpaId,
    leasesOk,
    expensesOk,
    unitsOk,
    leases,
    expensesResult?.expenses,
    units,
    taxYear,
    reset,
  ]);

  useEffect(() => {
    if (!isTaxPrepDemo) return;
    reset({
      properties: TAX_PREP_DEMO_VALUES.properties,
      totalRentCollected: TAX_PREP_DEMO_VALUES.totalRentCollected,
      expenses: TAX_PREP_DEMO_VALUES.expenses,
      userName: TAX_PREP_DEMO_VALUES.userName,
      userEmail: TAX_PREP_DEMO_VALUES.userEmail,
    });
    setCurrentStep(0);
  }, [isTaxPrepDemo, reset]);

  useEffect(() => {
    if (submitted || isTaxPrepDemo) return;
    const storageKey = cpaId || "default";
    try {
      const toSave = {
        formValues: {
          properties: formValues.properties ?? [{ address: "" }],
          totalRentCollected: formValues.totalRentCollected ?? "",
          expenses: formValues.expenses ?? [{ amount: "", category: "" }],
          userName: formValues.userName ?? "",
          userEmail: formValues.userEmail ?? "",
        },
        currentStep,
        subscribeToEmails,
      };
      localStorage.setItem(
        TAX_PREP_STORAGE_KEY(storageKey),
        JSON.stringify(toSave)
      );
    } catch (e) {
      console.warn("Could not save tax prep to storage:", e);
    }
  }, [
    cpaId,
    submitted,
    currentStep,
    formValues,
    subscribeToEmails,
    isTaxPrepDemo,
  ]);

  const {
    fields: propertyFields,
    append: appendProperty,
    remove: removeProperty,
  } = useFieldArray({
    control,
    name: "properties",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "expenses",
  });

  const validateStep = async (stepIndex) => {
    if (stepIndex === 0) {
      const hasValidProperty = (formValues.properties || []).some((p) =>
        p?.address?.trim()
      );
      if (!hasValidProperty) {
        message.error("Please add at least one property address.");
        return false;
      }
      return true;
    }

    let fieldsToValidate = [];
    switch (stepIndex) {
      case 1:
        fieldsToValidate = ["totalRentCollected"];
        break;
      case 2:
        fieldsToValidate = ["expenses"];
        break;
      case 3:
        fieldsToValidate = ["userName", "userEmail"];
        break;
      default:
        return true;
    }
    const isValid = await trigger(fieldsToValidate);
    return isValid;
  };

  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const expenses = data.expenses
        .filter((e) => e.amount && e.category)
        .map((e) => ({
          amount: parseFloat(String(e.amount).replace(/[^0-9.-]/g, "")) || 0,
          category: e.category,
        }));

      const properties = (data.properties || [])
        .filter((p) => p.address?.trim())
        .map((p) => p.address.trim());

      if (properties.length === 0) {
        message.error("Please add at least one property address.");
        setIsSubmitting(false);
        return;
      }

      const userEmail = formatEmailToLowercase(data.userEmail?.trim() || "");
      if (!userEmail) {
        message.error(
          "Your email is required so we can send your Schedule E summary."
        );
        setIsSubmitting(false);
        return;
      }

      const sendToUserOnly = !hasValidCpa;
      const payload = {
        ...(hasValidCpa ? { cpaId: cpaId.trim() } : { sendToUserOnly: true }),
        userName: data.userName?.trim() || "",
        userEmail,
        properties,
        propertyAddress: properties[0] || "",
        totalRentCollected:
          parseFloat(
            String(data.totalRentCollected).replace(/[^0-9.-]/g, "")
          ) || 0,
        expenses,
      };

      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:4000/api";
      const route = "/tax-prep/submit";
      const fullUrl = `${apiUrl}${route}`;
      console.log("[Tax Prep Submit]", {
        route,
        fullUrl,
        method: "POST",
        payload,
      });

      const result = await submitTaxPrep(payload);

      if (result.success) {
        setSubmitted(true);
        if (sendToUserOnly) setSubmittedToUserOnly(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
        try {
          localStorage.removeItem(TAX_PREP_STORAGE_KEY(cpaId || "default"));
        } catch (e) {
          // ignore
        }
        message.success(
          sendToUserOnly
            ? "Your Schedule E summary has been sent to your email."
            : "Your tax prep data has been sent to your CPA!",
          8
        );
        if (subscribeToEmails && payload.userEmail) {
          try {
            const apiUrl =
              process.env.REACT_APP_API_URL || "http://localhost:4000/api";
            await fetch(`${apiUrl}/marketing/subscribe`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email: payload.userEmail,
                firstName:
                  payload.userName?.trim()?.split(/\s+/)[0] || undefined,
                source: "tax_prep",
              }),
            });
          } catch (e) {
            console.warn("Tax prep email signup failed:", e);
          }
        }
      } else {
        const errMsg =
          result.message || "Something went wrong. Please try again.";
        console.warn("Tax prep submission failed:", result);
        message.error(errMsg);
      }
    } catch (err) {
      console.error("Tax prep submission error:", err);
      message.error(
        err?.message || "Failed to submit. Please try again or contact support."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <StepContent>
            <StepTitle>Properties</StepTitle>
            <StepDescription>
              List the street addresses of each rental property you owned in{" "}
              {taxYear}. Include full addresses (street, city, state, ZIP) as
              they appear on your deeds or insurance. Add every property that
              generated rental income, even if you sold it during the year.
            </StepDescription>
            <Spacer $marginTop="1.5rem" />
            {propertyFields.map((field, index) => (
              <PropertyRow key={field.id}>
                <FieldContainer>
                  <InputHeader>
                    Property Address{" "}
                    {propertyFields.length > 1 ? index + 1 : ""}
                  </InputHeader>
                  <PropertyInputRow>
                    <PropertyInputWrap>
                      <Controller
                        name={`properties.${index}.address`}
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder="e.g., 123 Main St, City, State ZIP"
                            size="large"
                          />
                        )}
                      />
                    </PropertyInputWrap>
                    {propertyFields.length > 1 && (
                      <RemoveButton
                        type="button"
                        onClick={() => removeProperty(index)}
                        aria-label="Remove property"
                      >
                        <CloseOutlined />
                      </RemoveButton>
                    )}
                  </PropertyInputRow>
                </FieldContainer>
              </PropertyRow>
            ))}
            <AddPropertyButton
              type="button"
              onClick={() => appendProperty({ address: "" })}
            >
              <PlusOutlined /> Add Another Property
            </AddPropertyButton>
          </StepContent>
        );

      case 1:
        return (
          <StepContent>
            <StepTitle>Income</StepTitle>
            <StepDescription>
              Enter the total rent you actually collected in {taxYear} across
              all properties listed above. Include monthly rent, prorated rent,
              and any additional rent (e.g., pet rent, parking). Do not include
              security deposits, application fees, or late fees. Only rental
              income.
            </StepDescription>
            <Spacer $marginTop="1.5rem" />
            <FieldContainer>
              <InputHeader htmlFor="totalRentCollected">
                Total Rents Collected ({taxYear})
              </InputHeader>
              <Controller
                name="totalRentCollected"
                control={control}
                rules={{
                  required: "Total rents collected is required",
                  validate: (v) => {
                    const num = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
                    if (isNaN(num) || num < 0) return "Enter a valid amount";
                    return true;
                  },
                }}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    id="totalRentCollected"
                    value={
                      field.value === "" || field.value === undefined
                        ? undefined
                        : parseFloat(
                            String(field.value).replace(/[^0-9.-]/g, "")
                          ) || undefined
                    }
                    onChange={(val) =>
                      field.onChange(
                        val !== null && val !== undefined ? String(val) : ""
                      )
                    }
                    placeholder="0"
                    min={0}
                    style={{ width: "100%" }}
                    size="large"
                    addonBefore="$"
                    formatter={(value) => currencyFormatter(value)}
                    parser={(value) => currencyParser(value)}
                  />
                )}
              />
              {errors.totalRentCollected && (
                <FormError>{errors.totalRentCollected.message}</FormError>
              )}
            </FieldContainer>
          </StepContent>
        );

      case 2:
        return (
          <StepContent>
            <StepTitle>Expenses</StepTitle>
            <StepDescription>
              Enter deductible rental expenses for {taxYear}. Use Schedule E
              categories: advertising, insurance, repairs, mortgage interest,
              etc. Include only expenses directly related to your rental
              properties (e.g., repairs, property taxes, utilities you paid).
            </StepDescription>
            <Spacer $marginTop="1.5rem" />
            {fields.map((field, index) => (
              <ExpenseRow key={field.id}>
                <ExpenseInputGroup>
                  <ExpenseAmountRow>
                    <FieldContainer>
                      <InputHeader>Amount</InputHeader>
                      <Controller
                        name={`expenses.${index}.amount`}
                        control={control}
                        render={({ field: expenseField }) => (
                          <InputNumber
                            {...expenseField}
                            value={
                              expenseField.value === "" ||
                              expenseField.value === undefined
                                ? undefined
                                : parseFloat(
                                    String(expenseField.value).replace(
                                      /[^0-9.-]/g,
                                      ""
                                    )
                                  ) || undefined
                            }
                            onChange={(val) =>
                              expenseField.onChange(
                                val !== null && val !== undefined
                                  ? String(val)
                                  : ""
                              )
                            }
                            placeholder="0"
                            min={0}
                            style={{ width: "100%" }}
                            size="large"
                            addonBefore="$"
                            formatter={(value) => currencyFormatter(value)}
                            parser={(value) => currencyParser(value)}
                          />
                        )}
                      />
                    </FieldContainer>
                  </ExpenseAmountRow>
                  <ExpenseCategoryWithRemove>
                    <FieldContainer>
                      <InputHeader>Category</InputHeader>
                      <Controller
                        name={`expenses.${index}.category`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            value={field.value || undefined}
                            onChange={(val) => field.onChange(val ?? "")}
                            placeholder="Select category"
                            style={{ width: "100%" }}
                            size="large"
                          >
                            {SCHEDULE_E_CATEGORIES.map((cat) => (
                              <Option key={cat.value} value={cat.value}>
                                {cat.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                      />
                    </FieldContainer>
                    {fields.length > 1 && (
                      <RemoveButton
                        type="button"
                        onClick={() => remove(index)}
                        aria-label="Remove expense"
                      >
                        <CloseOutlined />
                      </RemoveButton>
                    )}
                  </ExpenseCategoryWithRemove>
                </ExpenseInputGroup>
              </ExpenseRow>
            ))}
            <AddExpenseButton
              type="button"
              onClick={() => append({ amount: "", category: "" })}
            >
              <PlusOutlined /> Add Expense Row
            </AddExpenseButton>
          </StepContent>
        );

      case 3:
        return (
          <StepContent>
            <StepTitle>Contact Information</StepTitle>
            <StepDescription>
              {hasValidCpa
                ? "Your name and email so your CPA can reach you and you can be CC'd on the submission. Use the email where you want to receive the confirmation."
                : "Your name and email so we can send your Schedule E summary to you. Use the email where you want to receive the file."}
            </StepDescription>
            <Spacer $marginTop="1.5rem" />
            <FieldContainer>
              <InputHeader htmlFor="userName">Your Name</InputHeader>
              <Controller
                name="userName"
                control={control}
                rules={{ required: "Your name is required" }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="userName"
                    placeholder="Full name"
                    size="large"
                  />
                )}
              />
              {errors.userName && (
                <FormError>{errors.userName.message}</FormError>
              )}
            </FieldContainer>
            <Spacer $marginTop="1rem" />
            <FieldContainer>
              <InputHeader htmlFor="userEmail">Your Email</InputHeader>
              <Controller
                name="userEmail"
                control={control}
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email address",
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="userEmail"
                    type="email"
                    placeholder="you@example.com"
                    size="large"
                  />
                )}
              />
              {errors.userEmail && (
                <FormError>{errors.userEmail.message}</FormError>
              )}
            </FieldContainer>
          </StepContent>
        );

      case 4: {
        const validExpenses = (formValues.expenses || []).filter(
          (e) => e.amount && e.category
        );
        const totalExpenses = validExpenses.reduce(
          (sum, e) =>
            sum + (parseFloat(String(e.amount).replace(/[^0-9.-]/g, "")) || 0),
          0
        );
        // Aggregate by category so multiple expenses in same category show as one row with summed amount
        const expensesByCategory = validExpenses.reduce((acc, e) => {
          const cat = e.category || "Other Expenses";
          const amt =
            parseFloat(String(e.amount).replace(/[^0-9.-]/g, "")) || 0;
          acc[cat] = (acc[cat] || 0) + amt;
          return acc;
        }, {});
        const categoryTotals = Object.entries(expensesByCategory).map(
          ([category, amount]) => ({ category, amount })
        );

        const totalRent =
          parseFloat(
            String(formValues.totalRentCollected).replace(/[^0-9.-]/g, "")
          ) || 0;
        const netIncome = totalRent - totalExpenses;

        return (
          <StepContent>
            <StepTitle>Review & Submit</StepTitle>
            <StepDescription>
              {hasValidCpa
                ? "Review your properties, income, expenses, and contact info. Click Submit to send this data to your CPA via email. You will be CC'd. Your CPA will use this to prepare your Schedule E and tax return."
                : "Review your properties, income, expenses, and contact info. Click Submit to email yourself a Schedule E summary (Excel) for your records. You can use it for your taxes or share it with your preparer."}
            </StepDescription>
            <Spacer $marginTop="1.5rem" />
            <SummaryCard>
              <SummaryRow>
                <SummaryLabel>Properties</SummaryLabel>
                <SummaryValueColumn>
                  {(formValues.properties || []).filter((p) =>
                    p?.address?.trim()
                  ).length > 0 ? (
                    (formValues.properties || [])
                      .filter((p) => p?.address?.trim())
                      .map((p, i) => (
                        <PropertyLine key={i}>{p.address.trim()}</PropertyLine>
                      ))
                  ) : (
                    <span>N/A</span>
                  )}
                </SummaryValueColumn>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Total Rents ({taxYear})</SummaryLabel>
                <SummaryValue>
                  $
                  {totalRent.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                  })}
                </SummaryValue>
              </SummaryRow>
              <SummaryDivider />
              <SummarySection>Expenses</SummarySection>
              {categoryTotals.length > 0 ? (
                categoryTotals.map((e, i) => (
                  <SummaryRow key={i}>
                    <SummaryLabel>
                      {e.category === "Taxes" ? "Property Taxes" : e.category}
                    </SummaryLabel>
                    <SummaryValue>
                      $
                      {e.amount.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                      })}
                    </SummaryValue>
                  </SummaryRow>
                ))
              ) : (
                <SummaryRow>
                  <SummaryValue $muted>No expenses added</SummaryValue>
                </SummaryRow>
              )}
              <SummaryRow>
                <SummaryLabel>Total Expenses</SummaryLabel>
                <SummaryValue>
                  $
                  {totalExpenses.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                  })}
                </SummaryValue>
              </SummaryRow>
              <SummaryRow $highlight>
                <SummaryLabel>Net Income (Loss)</SummaryLabel>
                <SummaryValue>
                  $
                  {netIncome.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                  })}
                </SummaryValue>
              </SummaryRow>
              <Spacer $marginTop="1rem" />
              <SummaryRow>
                <SummaryLabel>Your Name</SummaryLabel>
                <SummaryValue>{formValues.userName || "N/A"}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Your Email</SummaryLabel>
                <SummaryValue>{formValues.userEmail || "N/A"}</SummaryValue>
              </SummaryRow>
              <Spacer $marginTop="1rem" />
              <EmailSignupCheckboxWrap>
                <Checkbox
                  checked={subscribeToEmails}
                  onChange={(e) => setSubscribeToEmails(e.target.checked)}
                >
                  <EmailSignupLabel>
                    Add me to the LandlordOps insider list for early access to
                    new features. Unsubscribe anytime.
                  </EmailSignupLabel>
                </Checkbox>
              </EmailSignupCheckboxWrap>
            </SummaryCard>
          </StepContent>
        );
      }

      default:
        return null;
    }
  };

  if (submitted) {
    if (submittedToUserOnly) {
      return (
        <PageContainer $success>
          <SuccessCard>
            <SuccessCheckmark>
              <CheckCircleOutlined />
            </SuccessCheckmark>
            <SuccessTitle>
              <SuccessTitleLine1>
                Your Schedule E summary has been sent
              </SuccessTitleLine1>
              <SuccessTitleLine2>to your email</SuccessTitleLine2>
            </SuccessTitle>
            <SuccessSubline style={{ marginTop: "0.5rem", fontWeight: 600 }}>
              Check your inbox for the Schedule E Excel file.
              <br />
              Use it for your records or share it with your tax preparer.
            </SuccessSubline>
            <Spacer $marginTop="2rem" />
            <UpsellCard>
              <UpsellHeadline>
                Tired of typing this out manually?
              </UpsellHeadline>
              <UpsellBody>
                Track your rental income and expenses in one place. Next year
                your Schedule E data is ready. Audit-ready from day one.
              </UpsellBody>
              <UpsellActions>
                <ButtonComponent
                  as={Link}
                  to="/"
                  width="100%"
                  className="tax-prep-primary-btn tax-prep-cta-btn"
                >
                  <span className="cta-desktop">Automate Your Portfolio</span>
                  <span className="cta-mobile">Automate Your Portfolio</span>
                </ButtonComponent>
              </UpsellActions>
            </UpsellCard>
          </SuccessCard>
        </PageContainer>
      );
    }

    const cpaDisplayName =
      formatCpaDisplayName(cpa) ||
      (cpaId
        ? cpaId.charAt(0).toUpperCase() + cpaId.slice(1).toLowerCase()
        : null) ||
      "your CPA";

    const nameValid =
      (cpa?.name || "").trim() &&
      !CPA_NAME_PLACEHOLDER.test((cpa?.name || "").trim());
    const companyValid =
      (cpa?.company || "").trim() &&
      !CPA_NAME_PLACEHOLDER.test((cpa?.company || "").trim());
    const companySuffix =
      nameValid && companyValid ? ` at ${(cpa.company || "").trim()}` : "";

    return (
      <PageContainer $success>
        <SuccessCard>
          <SuccessCheckmark>
            <CheckCircleOutlined />
          </SuccessCheckmark>
          <SuccessTitle>
            <SuccessTitleLine1>Your Schedule E has been sent</SuccessTitleLine1>
            <SuccessTitleLine2>
              to {cpaDisplayName}
              {companySuffix}
            </SuccessTitleLine2>
          </SuccessTitle>
          <SuccessSubline>You&apos;re CC&apos;d on the email</SuccessSubline>
          <SuccessSubline style={{ marginTop: "0.5rem", fontWeight: 600 }}>
            We&apos;ve sent the data to your CPA. Please check your inbox for
            the confirmation.
          </SuccessSubline>
          <Spacer $marginTop="2rem" />
          <UpsellCard>
            <UpsellHeadline>Tired of typing this out manually?</UpsellHeadline>
            <UpsellBody>
              {`${APP_NAME} is ${TAGLINE_SENTENCE}. It tracks your rental income and expenses in one place, and automates your tax prep. Next year your Schedule E data will be ready with a one-click export.`}
            </UpsellBody>
            <UpsellActions>
              <ButtonComponent
                as={Link}
                to="/"
                width="100%"
                className="tax-prep-primary-btn tax-prep-cta-btn"
              >
                <span className="cta-desktop">See How It Works</span>
                <span className="cta-mobile">See How It Works</span>
              </ButtonComponent>
            </UpsellActions>
          </UpsellCard>
        </SuccessCard>
      </PageContainer>
    );
  }

  if (loadingCpa) {
    return (
      <PageContainer>
        <TaxPrepHelmet taxYear={taxYear} />
        <TaxPrepContent>
          <HeaderSection>
            <PageHeader
              icon={FileTextOutlined}
              title={`${taxYear} Tax Prep`}
              subtitle="Gather your rental data and send it straight to your CPA"
            />
            <CpaLoadingContainer>
              <Spin size="large" />
              <CpaLoadingText>Verifying your CPA link…</CpaLoadingText>
              <CpaLoadingSubtext>
                Looking up your CPA in our system
              </CpaLoadingSubtext>
            </CpaLoadingContainer>
          </HeaderSection>
        </TaxPrepContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <TaxPrepHelmet taxYear={taxYear} cpa={hasValidCpa ? cpa : null} />
      <TaxPrepContent>
        <HeaderSection>
          <PageHeader
            icon={FileTextOutlined}
            title={`${taxYear} Tax Prep`}
            subtitle={
              hasValidCpa
                ? "Gather your rental data and send it straight to your CPA"
                : "Gather your rental data and get a Schedule E summary for your records"
            }
          />
          {hasValidCpa && (
            <CpaInfoBanner>
              <CpaInfoLabel>
                Upon completion, data you enter will be sent to:
              </CpaInfoLabel>
              <CpaInfoDetails>
                {cpa ? (
                  <>
                    <strong>{formatCpaDisplayName(cpa)}</strong>
                    {cpa.company && (
                      <>
                        {" · "}
                        {cpa.website ? (
                          <CpaInfoLink
                            href={cpa.website}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {cpa.company}
                          </CpaInfoLink>
                        ) : (
                          cpa.company
                        )}
                      </>
                    )}
                    {(cpa.address || "").trim() && (
                      <>
                        {" "}
                        ·{" "}
                        <CpaInfoText>{(cpa.address || "").trim()}</CpaInfoText>
                      </>
                    )}
                    {cpa.phone && (
                      <>
                        {" "}
                        ·{" "}
                        <CpaInfoLink href={`tel:${cpa.phone}`}>
                          {cpa.phone}
                        </CpaInfoLink>
                      </>
                    )}
                    {cpa.email && (
                      <>
                        {" "}
                        ·{" "}
                        <CpaInfoLink href={`mailto:${cpa.email}`}>
                          {cpa.email}
                        </CpaInfoLink>
                      </>
                    )}
                    {cpa.website && !cpa.company && (
                      <>
                        {" "}
                        ·{" "}
                        <CpaInfoLink
                          href={cpa.website}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Website
                        </CpaInfoLink>
                      </>
                    )}
                  </>
                ) : (
                  <strong>{cpaId}</strong>
                )}
              </CpaInfoDetails>
            </CpaInfoBanner>
          )}
          <TaxPrepNote>
            You can leave and come back anytime. Your progress is saved, so you
            can finish once you have all your figures.
          </TaxPrepNote>
          <TaxPrepDisclaimer>
            By using this tool you agree to our{" "}
            <Link to="/terms" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </Link>
            .
            {hasValidCpa &&
              " Whether a CPA will work with you is at the sole discretion of the CPA."}
          </TaxPrepDisclaimer>
        </HeaderSection>

        <WizardSection>
          {isTaxPrepDemo && (
            <Alert
              type="info"
              message="Demo mode"
              description="Form is pre-filled with sample data to showcase properties, income, expenses (including multiple in the same category), and contact info. Use ?debug=true in the URL to enable. Step through or submit to test the flow."
              style={{ marginBottom: 16 }}
            />
          )}
          <StepsBar>
            <Steps
              current={currentStep}
              size="small"
              direction="horizontal"
              responsive={false}
              className="tax-prep-steps"
            >
              {STEPS.map((s) => (
                <Step key={s.key} title={s.title} description={s.description} />
              ))}
            </Steps>
          </StepsBar>

          <WizardCard>
            <StepBody>{renderStepContent()}</StepBody>

            <StepFooter>
              {currentStep > 0 && (
                <ButtonComponent
                  variant="ghost"
                  onClick={handleBack}
                  width="100%"
                  className="tax-prep-back-btn"
                >
                  Back
                </ButtonComponent>
              )}
              {currentStep < 4 ? (
                <ButtonComponent
                  onClick={handleNext}
                  width="100%"
                  className="tax-prep-primary-btn"
                >
                  Next
                </ButtonComponent>
              ) : (
                <>
                  <ButtonComponent
                    variant="ghost"
                    onClick={() => {
                      const values = getValues();
                      const rawProperties =
                        values?.properties || formValues?.properties || [];
                      const props = rawProperties
                        .map((p) =>
                          typeof p?.address === "string"
                            ? p.address.trim()
                            : (p?.propertyAddress || "").trim() || null
                        )
                        .filter(Boolean);
                      const totalRent =
                        parseFloat(
                          String(
                            values?.totalRentCollected ??
                              formValues?.totalRentCollected ??
                              ""
                          ).replace(/[^0-9.-]/g, "")
                        ) || 0;
                      const rawExpenses =
                        values?.expenses || formValues?.expenses || [];
                      const exp = rawExpenses
                        .filter((e) => e?.amount && e?.category)
                        .map((e) => ({
                          amount:
                            parseFloat(
                              String(e.amount).replace(/[^0-9.-]/g, "")
                            ) || 0,
                          category: e.category,
                        }));
                      if (process.env.NODE_ENV === "development") {
                        console.log("[Tax Prep Download]", {
                          rawProperties,
                          extractedAddresses: props,
                          totalRent,
                          expenses: exp,
                        });
                      }
                      const pivot = buildTaxPrepPivot(props, totalRent, exp);
                      if (pivot) {
                        const ok = downloadScheduleETaxPacketExcel(
                          pivot,
                          String(new Date().getFullYear()),
                          { singleColumn: true }
                        );
                        if (ok) message.success("Schedule E downloaded.");
                        else
                          message.warning(
                            "No data to download. Add at least one property."
                          );
                      } else {
                        console.warn(
                          "[Tax Prep Download] No pivot - props:",
                          props
                        );
                        message.warning(
                          "Add at least one property address to download."
                        );
                      }
                    }}
                    width="100%"
                  >
                    <DownloadOutlined style={{ marginRight: 8 }} />
                    Download Schedule E (.xlsx)
                  </ButtonComponent>
                  <ButtonComponent
                    onClick={handleSubmit(onSubmit)}
                    isLoading={isSubmitting}
                    width="100%"
                    className="tax-prep-primary-btn"
                  >
                    Submit
                  </ButtonComponent>
                </>
              )}
            </StepFooter>
          </WizardCard>
        </WizardSection>
      </TaxPrepContent>
    </PageContainer>
  );
};

// ----- Styles -----
const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--color-bg-primary);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;
  box-sizing: border-box;

  ${(p) =>
    p.$success &&
    `
    justify-content: flex-start;
    padding: 1.25rem 2rem;
  `}

  @media ${device.mobile} {
    padding: ${(p) => (p.$success ? "1rem 1rem" : "1rem")};
    width: 95vw;
    min-width: ${LAYOUT_CONSTANTS.MIN_CONTENT_WIDTH};
    ${(p) => p.$success && "justify-content: flex-start; padding-top: 1rem;"}
  }

  @media (min-width: 768px) and (max-width: 1279px) {
    padding: ${(p) => (p.$success ? "1.5rem 1.5rem" : "1.5rem")};
    width: 90vw;
  }

  .tax-prep-primary-btn {
    color: white !important;
    padding-left: 2rem !important;
    padding-right: 2rem !important;
    min-width: 140px !important;
  }
`;

const TaxPrepContent = styled.div`
  width: 100%;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeaderSection = styled.div`
  width: 100%;
  align-self: stretch;
`;

const WizardSection = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
`;

const CpaInfoBanner = styled.div`
  background: rgba(44, 95, 45, 0.08);
  border: 1px solid rgba(44, 95, 45, 0.2);
  border-radius: 12px;
  padding: 1rem 1.5rem;
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;

  @media (min-width: 768px) {
    justify-content: center;
    text-align: center;
  }
`;

const CpaInfoLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
`;

const CpaInfoDetails = styled.span`
  font-size: 0.9375rem;
  color: var(--color-primary);
  font-family: var(--font-secondary);
  font-weight: 600;

  strong {
    color: var(--color-primary);
  }
`;

const CpaInfoText = styled.span`
  font-size: 0.9375rem;
  color: var(--color-primary);
  font-family: var(--font-secondary);
`;

const TaxPrepNote = styled.p`
  font-size: 0.875rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin-top: 0.75rem;
  margin-bottom: 0;
`;

const TaxPrepDisclaimer = styled.p`
  font-size: 0.8125rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin-top: 0.75rem;
  margin-bottom: 0;
  line-height: 1.4;

  a {
    color: var(--color-primary);
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const CpaInfoLink = styled.a`
  color: var(--color-primary);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CpaLinkAlert = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, #fff8e1 0%, #fff3c4 100%);
  border: 2px solid #ffb74d;
  border-radius: 16px;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 12px rgba(255, 183, 77, 0.15);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #ff9800 0%, #ffb74d 50%, #ffc107 100%);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 183, 77, 0.25);
    transition: all 0.3s ease;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.5rem;
    margin-bottom: 1.5rem;

    &:hover {
      transform: none;
    }
  }
`;

const CpaLinkIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #ff9800 0%, #ffb74d 100%);
  border-radius: 50%;
  box-shadow: 0 3px 8px rgba(255, 152, 0, 0.3);
  flex-shrink: 0;

  .anticon {
    font-size: 24px;
    color: white;
  }

  @media (max-width: 768px) {
    order: 1;
    align-self: center;
    width: 40px;
    height: 40px;

    .anticon {
      font-size: 20px;
    }
  }
`;

const CpaLinkContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (max-width: 768px) {
    width: 100%;
    order: 2;
  }
`;

const CpaLinkDescription = styled.div`
  font-size: 0.95rem;
  color: #ef4444 !important;
  line-height: 1.5;
  font-weight: 500;
  text-align: left;

  strong {
    color: #ef4444 !important;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const CpaLinkLineBreak = styled.span`
  display: inline;

  @media (max-width: 768px) {
    display: block;
  }
`;

const CpaLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 3rem 2rem;
  background: rgba(44, 95, 45, 0.06);
  border-radius: 12px;
  border: 1px solid rgba(44, 95, 45, 0.15);
  margin-top: 1rem;
`;

const CpaLoadingText = styled.p`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text);
  font-family: var(--font-secondary);
`;

const CpaLoadingSubtext = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
`;

const CpaRequiredCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.75rem 2rem;
  border: 2px solid var(--color-border-light);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  width: 100%;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CpaRequiredTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 0.75rem 0;
  font-family: var(--font-primary);
`;

const CpaRequiredBody = styled.p`
  font-size: 0.95rem;
  color: var(--color-text);
  line-height: 1.6;
  margin: 0 0 0.5rem 0;
  font-family: var(--font-secondary);

  strong {
    color: var(--color-primary);
  }
`;

const CpaRequiredContact = styled.div`
  font-size: 0.9375rem;
  margin-bottom: 0.75rem;
  font-family: var(--font-secondary);
`;

const CpaContactLink = styled.a`
  color: var(--color-primary);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CpaContactSep = styled.span`
  color: var(--color-text-light);
`;

const CpaRequiredWebsite = styled.a`
  display: inline;
  font-size: 0.9375rem;
  color: var(--color-primary);
  text-decoration: none;
  font-family: var(--font-secondary);

  &:hover {
    text-decoration: underline;
  }
`;

const CpaRequiredCta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
`;

const StepsBar = styled.div`
  width: 100%;
  margin: 0 0 1.5rem 0;
  padding: 1rem 0;
  display: flex;
  flex: 1;
  min-width: 0;

  /* Force Ant Design Steps to fill full width */
  .tax-prep-steps {
    margin-bottom: 0;
    width: 100% !important;
    max-width: 100%;
    flex: 1;
    min-width: 0;
  }

  .tax-prep-steps.ant-steps-horizontal {
    width: 100% !important;
  }

  .tax-prep-steps.ant-steps-horizontal .ant-steps-item {
    flex: 1;
    min-width: 0;
  }

  /* Hide connecting lines between steps */
  .tax-prep-steps .ant-steps-item-tail {
    display: none !important;
  }
  .tax-prep-steps .ant-steps-item-title::after {
    display: none !important;
  }

  /* Desktop: icon left, text right of number circle */
  @media (min-width: 768px) {
    .tax-prep-steps .ant-steps-item-container {
      display: flex !important;
      align-items: center !important;
      flex-direction: row !important;
    }
    .tax-prep-steps .ant-steps-item-icon {
      flex-shrink: 0 !important;
      margin-inline-end: 0.5rem !important;
    }
    .tax-prep-steps .ant-steps-item-content {
      display: block !important;
      text-align: left !important;
      margin-top: 0 !important;
      flex: 1 !important;
      min-width: 0 !important;
    }
  }

  .tax-prep-steps .ant-steps-item-title {
    font-weight: 600;
    font-family: var(--font-secondary);
  }

  .tax-prep-steps .ant-steps-item-description {
    font-family: var(--font-secondary);
    font-size: 12px;
  }

  .tax-prep-steps .ant-steps-item-icon {
    width: 32px !important;
    height: 32px !important;
    line-height: 32px !important;
    font-size: 14px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .tax-prep-steps .ant-steps-item-process .ant-steps-item-icon {
    background: var(--color-primary) !important;
    border-color: var(--color-primary) !important;
    color: white !important;
  }

  .tax-prep-steps .ant-steps-item-process .ant-steps-item-icon .ant-steps-icon {
    color: white !important;
  }

  .tax-prep-steps .ant-steps-item-finish .ant-steps-item-icon {
    background: var(--color-primary) !important;
    border-color: var(--color-primary) !important;
    color: white !important;
  }

  .tax-prep-steps .ant-steps-item-finish .ant-steps-item-icon .ant-steps-icon,
  .tax-prep-steps .ant-steps-item-finish .ant-steps-item-icon .anticon,
  .tax-prep-steps .ant-steps-item-finish .ant-steps-item-icon svg {
    color: white !important;
    fill: white !important;
  }

  @media ${device.mobile} {
    .tax-prep-steps {
      margin-bottom: 0;
    }
    /* Compact horizontal steps on mobile: smaller icons, tighter text, full width */
    .tax-prep-steps .ant-steps-item-icon {
      width: 28px !important;
      height: 28px !important;
      line-height: 28px !important;
      font-size: 12px !important;
    }
    .tax-prep-steps .ant-steps-item-title {
      font-size: 12px;
    }
    .tax-prep-steps .ant-steps-item-description {
      font-size: 10px;
    }
    .tax-prep-steps .ant-steps-item {
      flex: 1;
      min-width: 0;
      padding-inline: 2px !important;
    }
  }
`;

const WizardCard = styled(Card)`
  position: relative;
  z-index: 1;
  max-width: 900px;
  width: 100%;
  border-radius: 32px;
  box-shadow:
    0 20px 60px var(--shadow-light),
    0 8px 25px rgba(44, 95, 45, 0.1);
  border: 1px solid var(--color-border-light);
  overflow: hidden;
  box-sizing: border-box;

  /* Back button in steps: no border */
  .tax-prep-back-btn {
    border: none !important;
  }

  /* Form validation errors - red to match rest of app */
  ${FormError} {
    color: #ef4444 !important;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
  }

  .ant-card-body {
    padding: 3rem 2.5rem;
  }

  .ant-input,
  .ant-select-selector {
    border-radius: 16px !important;
    border: 2px solid var(--color-border-light) !important;
    font-family: var(--font-secondary) !important;
  }

  .ant-input:focus,
  .ant-select-focused .ant-select-selector {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 4px rgba(44, 95, 45, 0.1) !important;
  }

  @media ${device.mobile} {
    border-radius: 20px;

    .ant-card-body {
      padding: 1.5rem 1.25rem;
    }

    .ant-input,
    .ant-select-selector {
      border-radius: 12px !important;
    }

    .ant-input-lg,
    .ant-select-lg .ant-select-selector {
      min-height: 44px !important;
    }
  }
`;

const StepBody = styled.div`
  min-height: 180px;

  @media ${device.mobile} {
    min-height: 160px;
  }
`;

const StepContent = styled.div`
  padding-bottom: 0.5rem;
`;

const StepTitle = styled.h2`
  font-size: 1.5rem;
  font-family: var(--font-primary);
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem;
  max-width: 36rem;
  margin-left: auto;
  margin-right: auto;
  text-align: center;

  @media ${device.mobile} {
    font-size: 1.25rem;
  }
`;

const StepDescription = styled.p`
  font-size: 1rem;
  font-family: var(--font-secondary);
  color: var(--color-text-light);
  line-height: 1.5;
  max-width: 40rem;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 1.5rem;

  @media ${device.mobile} {
    font-size: 0.9375rem;
  }
`;

const FieldContainer = styled.div`
  margin-bottom: 0.5rem;
  width: 100%;
  min-width: 0;
`;

const PropertyRow = styled.div`
  margin-bottom: 1rem;
`;

const PropertyInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PropertyInputWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const AddPropertyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--color-bg-tertiary);
  border: 1px dashed var(--color-border-medium);
  border-radius: 12px;
  color: var(--color-text);
  font-weight: 500;
  font-family: var(--font-secondary);
  cursor: pointer;
  margin-top: 0.5rem;

  &:hover {
    background: rgba(44, 95, 45, 0.06);
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
`;

const ExpenseRow = styled.div`
  margin-bottom: 1.5rem;
`;

const ExpenseInputGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  @media ${device.mobile} {
    flex-direction: column;
    gap: 1rem;
  }
`;

const ExpenseCategoryWithRemove = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;

  > ${FieldContainer} {
    flex: 1;
    min-width: 0;
  }

  > button {
    margin-bottom: 6px;
  }

  @media ${device.mobile} {
    flex: none;
    width: 100%;
  }
`;

const ExpenseAmountRow = styled.div`
  display: flex;
  flex: 1;
  gap: 0.5rem;
  align-items: center;
  min-width: 0;

  > ${FieldContainer}:first-child {
    flex: 1;
    min-width: 0;
  }

  @media ${device.mobile} {
    width: 100%;
    min-width: 0;
  }
`;

const RemoveButton = styled.button`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 12px;
  color: var(--color-text-light);
  cursor: pointer;
  font-family: var(--font-secondary);

  @media ${device.mobile} {
    width: 36px;
    height: 36px;
    min-width: 36px;
    border-radius: 8px;
  }

  &:hover {
    background: var(--color-error);
    color: white;

    .anticon,
    svg {
      color: white !important;
      fill: white !important;
    }
  }

  &:active {
    background: var(--color-error);
    color: white;
  }
`;

const AddExpenseButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--color-bg-tertiary);
  border: 1px dashed var(--color-border-medium);
  border-radius: 12px;
  color: var(--color-text);
  font-weight: 500;
  font-family: var(--font-secondary);
  cursor: pointer;
  margin-top: 0.5rem;

  &:hover {
    background: rgba(44, 95, 45, 0.06);
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
`;

const StepFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-border-light);
  width: 100%;

  @media ${device.mobile} {
    margin-top: 1.25rem;
    padding-top: 1.25rem;
  }
`;

const SummaryCard = styled.div`
  background: var(--color-bg-tertiary);
  border-radius: 16px;
  padding: 1.5rem;

  @media ${device.mobile} {
    padding: 1.25rem;
    border-radius: 12px;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  padding: 0.5rem 0;
  font-family: var(--font-secondary);
  ${(p) =>
    p.$highlight &&
    "font-weight: 600; background: rgba(44, 95, 45, 0.08); margin: 0 -1rem; padding: 0.75rem 1rem; border-radius: 12px;"}

  @media ${device.mobile} {
    flex-wrap: wrap;
    ${(p) => p.$highlight && "margin: 0 -0.75rem; padding: 0.625rem 0.75rem;"}
  }
`;

const SummaryLabel = styled.span`
  color: var(--color-text-light);
  font-size: 0.9375rem;
  font-family: var(--font-secondary);
  flex-shrink: 0;

  @media ${device.mobile} {
    font-size: 0.875rem;
  }
`;

const SummaryValueColumn = styled.div`
  font-weight: 600;
  color: var(--color-text);
  font-family: var(--font-secondary);
  text-align: right;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;

  @media ${device.mobile} {
    font-size: 0.9375rem;
  }
`;

const PropertyLine = styled.span`
  display: block;
`;

const SummaryValue = styled.span`
  font-weight: 600;
  color: var(--color-text);
  font-family: var(--font-secondary);
  text-align: right;
  word-break: break-word;
  min-width: 0;
  ${(p) => p.$muted && "color: var(--color-text-light); font-weight: 400;"}

  @media ${device.mobile} {
    font-size: 0.9375rem;
  }
`;

const SummaryDivider = styled.hr`
  margin: 1.25rem 0;
  border: none;
  border-top: 2px solid #cbd5e0;
  width: 100%;
`;

const SummarySection = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-light);
  margin-top: 0.5rem;
  padding-top: 0;
`;

const EmailSignupCheckboxWrap = styled.div`
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-border, #e5e7eb);
  text-align: left;

  .ant-checkbox-wrapper {
    align-items: flex-start;
  }

  .ant-checkbox-inner {
    width: 22px;
    height: 22px;
    border: 2px solid var(--color-border, #d9d9d9) !important;
    border-radius: 4px;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    border-color: var(--color-primary, #2c5f2d) !important;
  }

  .ant-checkbox-checked .ant-checkbox-inner::after {
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) rotate(45deg) !important;
  }

  .ant-checkbox + span {
    padding-left: 12px;
  }
`;

const EmailSignupLabel = styled.span`
  font-size: 0.875rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  line-height: 1.4;
  text-align: left;
  display: block;
`;

const SuccessCard = styled(Card)`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  border-radius: 32px;
  box-shadow:
    0 20px 60px var(--shadow-light),
    0 8px 25px rgba(44, 95, 45, 0.1);
  border: 1px solid var(--color-border-light);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
  }

  .ant-card-body {
    padding: 2.5rem 2.75rem;
  }

  @media (min-width: 768px) and (max-width: 1279px) {
    max-width: 640px;
    border-radius: 24px;

    .ant-card-body {
      padding: 2.25rem 2rem;
    }
  }

  @media ${device.mobile} {
    border-radius: 20px;
    max-width: 100%;

    .ant-card-body {
      padding: 2rem 1.25rem;
    }
  }
`;

const SuccessCheckmark = styled.div`
  font-size: 4rem;
  color: var(--color-primary);
  margin-bottom: 0.5rem;

  .anticon {
    font-size: 4rem;
  }

  @media (min-width: 768px) and (max-width: 1279px) {
    font-size: 3.5rem;
    margin-bottom: 0.25rem;
    .anticon {
      font-size: 3.5rem;
    }
  }

  @media ${device.mobile} {
    font-size: 3rem;
    margin-bottom: 0.25rem;
    .anticon {
      font-size: 3rem;
    }
  }
`;

const SuccessTitle = styled.h2`
  font-size: 1.75rem;
  font-family: var(--font-primary);
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0;
  line-height: 1.4;
  letter-spacing: -0.01em;
  max-width: 100%;

  @media (min-width: 768px) and (max-width: 1279px) {
    font-size: 1.625rem;
  }

  @media ${device.mobile} {
    font-size: 1.375rem;
    line-height: 1.35;
  }
`;

const SuccessTitleLine1 = styled.span`
  display: block;
  font-size: inherit;
  font-weight: inherit;
  font-family: inherit;
  color: inherit;
`;

const SuccessTitleLine2 = styled.span`
  display: block;
  font-size: inherit;
  font-weight: inherit;
  font-family: inherit;
  color: inherit;
`;

const SuccessSubline = styled.p`
  font-size: 1rem;
  font-family: var(--font-secondary);
  color: var(--color-text-light);
  margin-top: 0.5rem;
  margin-bottom: 0;

  @media ${device.mobile} {
    font-size: 0.9375rem;
  }
`;

const UpsellCard = styled.div`
  text-align: left;
  padding: 1.5rem;
  background: linear-gradient(
    135deg,
    rgba(44, 95, 45, 0.05) 0%,
    rgba(74, 124, 89, 0.05) 100%
  );
  border: 1px solid rgba(44, 95, 45, 0.2);
  border-radius: 16px;
  margin-top: 2rem;

  @media (min-width: 768px) and (max-width: 1279px) {
    padding: 1.5rem 1.75rem;
    margin-top: 1.75rem;
    border-radius: 14px;
  }

  @media ${device.mobile} {
    padding: 1.25rem;
    margin-top: 1.5rem;
    border-radius: 12px;
  }
`;

const UpsellHeadline = styled.h3`
  font-size: 1.25rem;
  font-family: var(--font-primary);
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.75rem;
  line-height: 1.3;
  text-align: center;

  @media (min-width: 768px) and (max-width: 1279px) {
    font-size: 1.1875rem;
  }

  @media ${device.mobile} {
    font-size: 1.125rem;
  }
`;

const UpsellBody = styled.p`
  font-size: 1rem;
  font-family: var(--font-secondary);
  color: var(--color-text-light);
  line-height: 1.5;
  margin-bottom: 1.25rem;

  @media (min-width: 768px) and (max-width: 1279px) {
    font-size: 0.9375rem;
    margin-bottom: 1.125rem;
  }

  @media ${device.mobile} {
    font-size: 0.9375rem;
    margin-bottom: 1rem;
  }
`;

const UpsellActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: stretch;

  .tax-prep-cta-btn,
  .tax-prep-cta-btn * {
    color: white !important;
  }
  .tax-prep-cta-btn .cta-mobile {
    display: none;
  }
  .tax-prep-cta-btn .cta-desktop {
    display: inline;
  }
  @media ${device.mobile} {
    .tax-prep-cta-btn .cta-mobile {
      display: inline;
    }
    .tax-prep-cta-btn .cta-desktop {
      display: none;
    }
  }
`;

const BackToHomeLink = styled(Link)`
  font-size: 1rem;
  font-family: var(--font-secondary);
  font-weight: 500;
  color: var(--color-text-light);
  text-decoration: none;
  text-align: center;
  padding: 0.5rem 0;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--color-primary);
    text-decoration: underline;
  }

  @media ${device.mobile} {
    padding: 0.625rem 0;
  }
`;

export default TaxPrepPage;
